import React, { Fragment, useState, useEffect, useContext } from 'react';
import TableBuilder, { ApiRequest, ModalButtonBuilder, RegisteredInputsBuilder, ToastBuilder, ButtonBuilder } from '../../../../../../../GlobalComponents/Builders';
import { buildSelectOptionsFromArray, sortArrayByKeys } from '../../../../../../../GlobalComponents/Helpers';
import { Container, Row, Card, CardBody, Form } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AccountContext } from '../../../../../../../GlobalComponents/Authentication/Accounts';
import { ContentLoader } from '../../../../../../../Layout/Loader';
import moment from 'moment';

const DMTAssessments = () => {

    const { userSession, portalEmulation, isAdmin } = useContext(AccountContext);
    const navigate = useNavigate();
    const { register, handleSubmit, control, setValue, formState: { errors, isValid } } = useForm();

    const [assessments, setAssessments] = useState([]);
    const [isProcessing, setIsProcessing] = useState(true);
    const [partners, setPartners] = useState([]);
    const [users, setUsers] = useState([]);

    const loadAssessments = () => {
        ApiRequest('GET', '/dmt/assessments', { setIsProcessing }).then((results) => {
            setAssessments(results.data?.length > 0 ? sortArrayByKeys(results.data, [{ updated_at: 'DESC' }]) : []);
        });
    };

    const handleCreate = (formData) => {
        ApiRequest('POST', '/dmt/assessments', {
            body: {
                name: formData.name,
                description: formData.description,
                status: 'draft',
                partner_id: formData.partner_id || 0,
                created_by: formData.created_by || userSession?.['custom:userId'] || null,
            },
        }).then((results) => {
            const assessmentId = results.data?.assessment_id || results.data;
            if (assessmentId) {
                ToastBuilder('success', 'Assessment Created');
                navigate(`${process.env.PUBLIC_URL}/data-migration/assessments/${assessmentId}`);
            } else {
                ToastBuilder('error', 'Unable to create assessment');
            }
        });
    };

    useEffect(() => {
        document.title = 'Discovery Assessments';
        loadAssessments();
        ApiRequest('GET', '/partners', { queryParams: { fields: 'id,name' } }).then((results) => {
            setPartners(results.data?.length > 0 ? results.data : []);
        });
        ApiRequest('GET', '/users', { queryParams: { fields: 'user_id,full_name' } }).then((results) => {
            setUsers(results.data?.length > 0 ? results.data : []);
            const currentUserId = userSession?.['custom:userId'];
            if (currentUserId && results.data?.length > 0) {
                const match = results.data.find(u => String(u.user_id) === String(currentUserId));
                if (match) setValue('created_by', match.user_id);
            }
        });
    }, []);

    const statusColors = {
        draft: 'secondary',
        in_progress: 'primary',
        awaiting_input: 'warning',
        completed: 'success',
        archived: 'dark',
    };

    return (
        <Fragment>
            <Row>
                <Container>
                    <Card>
                        <CardBody>
                            <TableBuilder
                                data={assessments}
                                processing={isProcessing}
                                title='Discovery Assessments'
                                pageSize={25}
                                subHeader={true}
                                columnsToSearch={['name', 'status', 'created_by_name', 'partner_name']}
                                actions={
                                    <ModalButtonBuilder
                                        title='New Assessment'
                                        className='btn-primary'
                                        formId='create-assessment-form'
                                        formIsValid={isValid}
                                        style={{ maxWidth: 550 }}
                                        body={
                                            <Form id='create-assessment-form' onSubmit={handleSubmit(handleCreate)}>
                                                <RegisteredInputsBuilder
                                                    registrator={register}
                                                    registratorErrors={errors}
                                                    control={control}
                                                    inputs={[
                                                        {
                                                            label: 'Name',
                                                            labelSize: 3,
                                                            name: 'name',
                                                            required: true,
                                                            placeholder: 'Entech US - Autotask to CWM',
                                                        },
                                                        {
                                                            label: 'Description',
                                                            labelSize: 3,
                                                            name: 'description',
                                                            type: 'textarea',
                                                            placeholder: 'Migration scoping for Entech US',
                                                            settings: { resize: true },
                                                            style: { height: 80 },
                                                        },
                                                        {
                                                            label: 'Partner',
                                                            labelSize: 3,
                                                            name: 'partner_id',
                                                            type: 'reactselect',
                                                            required: true,
                                                            options: buildSelectOptionsFromArray(partners, { label: 'name', value: 'id' }),
                                                        },
                                                        {
                                                            label: 'Created By',
                                                            labelSize: 3,
                                                            name: 'created_by',
                                                            type: 'reactselect',
                                                            options: buildSelectOptionsFromArray(users, { label: 'full_name', value: 'user_id' }),
                                                        },
                                                    ]}
                                                />
                                            </Form>
                                        }
                                    />
                                }
                                columns={[
                                    {
                                        name: 'ID',
                                        selector: (row) => row.assessment_id,
                                        sortable: true,
                                        center: true,
                                        maxWidth: '7%',
                                    },
                                    {
                                        name: 'Name',
                                        selector: (row) => (
                                            <Link to={`${process.env.PUBLIC_URL}/data-migration/assessments/${row.assessment_id}`}>
                                                {row.name}
                                            </Link>
                                        ),
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Partner',
                                        selector: (row) => row.partner_name || '-',
                                        sortable: true,
                                        center: true,
                                        maxWidth: '15%',
                                    },
                                    {
                                        name: 'Status',
                                        selector: (row) => (
                                            <span className={`badge bg-${statusColors[row.status] || 'secondary'}`}>
                                                {(row.status || 'draft').replace('_', ' ')}
                                            </span>
                                        ),
                                        sortable: true,
                                        center: true,
                                        maxWidth: '14%',
                                    },
                                    {
                                        name: 'Linked Jobs',
                                        selector: (row) => row.job_count || 0,
                                        sortable: true,
                                        center: true,
                                        maxWidth: '12%',
                                    },
                                    {
                                        name: 'Created By',
                                        selector: (row) => row.created_by_name || row.created_by || '-',
                                        sortable: true,
                                        center: true,
                                        maxWidth: '15%',
                                    },
                                    {
                                        name: 'Updated',
                                        selector: (row) => row.updated_at ? moment.utc(row.updated_at * 1000).fromNow() : '-',
                                        sortable: true,
                                        center: true,
                                        maxWidth: '14%',
                                    },
                                    {
                                        name: 'View',
                                        selector: (row) => (
                                            <Link to={`${process.env.PUBLIC_URL}/data-migration/assessments/${row.assessment_id}`}>
                                                View / Edit
                                            </Link>
                                        ),
                                        center: true,
                                        maxWidth: '12%',
                                    },
                                ]}
                            />
                        </CardBody>
                    </Card>
                </Container>
            </Row>
        </Fragment>
    );
};

export default DMTAssessments;
